import type { AssetWorkflowOptions } from '../../types';

export const addFulfillmentContextLinkPropertiesWorkflow: AssetWorkflowOptions = {
  workflowKey: 'addFulfillmentContextLinkPropertiesWorkflow',
  workflowType: 'addAssetWorkflow',
  workflowConditionalMatcher: [
    {
      assetType: 'carrierAccount',
      schema: {
        type: 'array',
        minItems: 1,
      },
    },
  ],
  assetType: 'fulfillmentContextLinkProperty',
  workflowIndex: 2,
  workflowName: 'Add a new Carrier Selection Override',
  workflowDescription: `

### Pause
A carrier account or service method that has been paused will not be considered when Shiptime rate shops for a label.

### Test Mode
A carrier account or service method in test mode will not generate production labels. Instead, test labels will be generated. 
Setting this when you are initially testing a new carrier is a good way to validate that everything is working in and out of the fulfillment center.

This workflow will allow you to create a new override for one of your carrier accounts or service methods.
  `,
  workflowStepOptions: [
    {
      workflowStepKey: 'linkPropertyType',
      workflowStepNavigationName: 'Override Type',
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
        index: 2,
        prefix: '- (',
        suffix: ' Override)',
      },
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'What type of override would you like to configure?',
        description: 'You can setup an override to pause a carrier account or service method or set it to test mode.',
        help: `
### Paused
A carrier account or service method that has been paused will not be considered when Shiptime rate shops for a label.

### Test Mode
A carrier account or service method in test mode will not generate production labels. Instead, test labels will be generated. 
Setting this when you are initially testing a new carrier is a good way to validate that everything is working in and out of the fulfillment center.
          `,
        options: [
          { optionText: 'Pause', optionValue: 'paused' },
          { optionText: 'Test Mode', optionValue: 'test_mode' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'propertyType',
      },
    },
    {
      workflowStepKey: 'carrierAccountOrServiceMethod',
      workflowStepNavigationName: 'Override Scope',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Will this override apply to a carrier account or service method?',
        description:
          'Overrides that apply to a carrier account will apply to all service methods for the specfied account.  A service method override applies only to a single service method.',
        help: ``,
        options: [
          { optionText: 'Carrier Account', optionValue: 'carrierAcount' },
          { optionText: 'Carrier Service Method', optionValue: 'carrierServiceMethod' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
    {
      workflowStepKey: 'carrierAccountSelect',
      workflowStepNavigationName: 'Carrier Account Select',
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
        index: 1,
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierAccountOrServiceMethod',
          schema: { type: 'string', const: 'carrierAcount' },
        },
      ],
      workflowComponent: {
        workflowComponentType: 'carrierAccountSelect',
        prompt: 'Which a carrier account will this override apply to?',
        description: 'Note: the override will apply to *ALL* service methods configured for this carrier account.',
        help: '',
      },
      calculatedComponentProps: {
        carrierId: {
          type: 'workflowStep',
          property: 'carrierSelect',
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'carrierAccountId',
      },
    },

    {
      workflowStepKey: 'serviceMethodSelect',
      workflowStepNavigationName: 'Carrier Service Method Select',
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
        index: 1,
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierAccountOrServiceMethod',
          schema: { type: 'string', const: 'carrierServiceMethod' },
        },
      ],
      workflowComponent: {
        workflowComponentType: 'carrierServiceMethodEntry',
        prompt: 'Which service method will this override apply to?',
        singleSelect: true,
        hideCarriers: false,
        description: 'You can only setup a single service method per override.',
        help: ``,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'carrierServiceMethodId',
      },
    },

    {
      workflowStepKey: 'includeOrigin',
      workflowStepNavigationName: 'Apply to All Origins',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Does this override apply to all origins?',
        description: 'You will be prompted to select an origin if you select no.',
        help: ``,
        options: [
          { optionText: 'Yes', optionValue: true },
          { optionText: 'No', optionValue: false },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
    {
      workflowStepKey: 'origin',
      workflowStepNavigationName: 'Origin',
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'includeOrigin',
          schema: { type: 'boolean', const: false },
        },
      ],
      workflowComponent: {
        workflowComponentType: 'assetSetSelect',
        prompt: 'Which origin should this override apply to?',
        description: '',
        help: '',
        assetType: 'origin',
        singleSelect: true,
        assetIdProperty: 'originId',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'originId',
      },
    },
    {
      workflowStepKey: 'useTenant',
      workflowStepNavigationName: 'All Tenants',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Does this override apply to all tenants?',
        description: 'You will be prompted to select a tenant if you select no.',
        help: ``,
        options: [
          { optionText: 'Yes', optionValue: true },
          { optionText: 'No', optionValue: false },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
    {
      workflowStepKey: 'tenantSelect',
      workflowStepNavigationName: 'Tenant',
      workflowComponent: {
        description: '',
        workflowComponentType: 'assetSetSelect',
        prompt: 'Which tenant should this override apply to?',
        help: '',
        assetType: 'tenant',
        singleSelect: true,
        assetIdProperty: 'tenantId',
      },
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'useTenant',
          schema: { type: 'boolean', const: false },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'tenantId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'Enter a name for this Test Mode Override',
        description: ``,
        help: '',
        defaultValue: (workflowState) => {
          const linkType = workflowState.linkPropertyType === 'paused' ? 'Pause Override' : 'Test Mode Override';
          return `${linkType}`;
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'Description',
      workflowStepNavigationName: 'Description',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 10,
        prompt: 'Enter a description (optional):',
        description: ``,
        defaultValue: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    {
      workflowStepKey: 'summary',
      workflowStepNavigationName: 'Configuration Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Add Fulfillment Context Summary',
        description: 'The FC Context will be created.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
